/**
 * Landing Module - Innholdskonfigurasjon (Refaktorert v2.0)
 * Bruker shared komponenter og modulbeskrivelser
 */
import React from 'react';
import { 
  FileText, Users, Clock, Sparkles, Shield, 
  Building2, Briefcase, Home, Map, ScrollText,
  Heart, Scale, Gavel, Info, CheckCircle
} from 'lucide-react';

// Import modulbeskrivelser
import { ArbeidsrettDescription } from '../../../components/modules/arbeidsrett';
import { HusleierettDescription } from '../../../components/modules/husleierett';
import { ForbrukerrettDescription } from '../../../components/modules/forbrukerrett';
import { BarnevernDescription } from '../../../components/modules/barnevern';
import { ForvaltningsrettDescription } from '../../../components/modules/forvaltningsrett';
import { EiendomsrettDescription } from '../../../components/modules/eiendomsrett';
import { ArverettDescription } from '../../../components/modules/arverett';
import { TrygderettDescription } from '../../../components/modules/trygderett';

// ============================================================
// RETTSOMRÅDE-MODULER
// ============================================================
export const LAW_MODULES = [
  // AKTIVE MODULER
  {
    id: 'barnevern',
    title: 'Barnevern',
    description: 'AI-drevet vedtaksanalyse og klagegenerering',
    shortSell: 'Forstå vedtak. Beskytt dine rettigheter.',
    icon: 'Users',
    status: 'active',
    route: '/barnevern',
    badge: 'Master'
  },
  {
    id: 'forbrukerrett',
    title: 'Forbrukerrett',
    description: 'Reklamasjon med OCR og fristberegning',
    shortSell: 'Fra kvittering til kompensasjon.',
    icon: 'Scale',
    status: 'active',
    route: '/documents',
    badge: 'Master'
  },
  {
    id: 'arbeidsrett',
    title: 'Arbeidsrett',
    description: 'Kontrakter, oppsigelse og AI-analyse',
    shortSell: 'Master-grade juridisk kontroll.',
    icon: 'Briefcase',
    status: 'active',
    route: '/arbeidsrett',
    badge: 'Master'
  },
  {
    id: 'husleierett',
    title: 'Husleierett',
    description: 'Leiekontrakt og depositumsverktøy',
    shortSell: 'Master-grade for leieforhold.',
    icon: 'Home',
    status: 'active',
    route: '/husleierett',
    badge: 'Master'
  },
  // NYE MODULER
  {
    id: 'forvaltningsrett',
    title: 'Forvaltningsrett',
    description: 'Dine rettigheter i møte med staten',
    shortSell: 'Klage på vedtak, innsyn og svarfrister.',
    icon: 'Building2',
    status: 'active',
    route: '/forvaltningsrett',
    badge: 'Ny'
  },
  {
    id: 'eiendomsrett',
    title: 'Eiendomsrett',
    description: 'Bolig, naboforhold og husleie',
    shortSell: 'Nabovarsel, boligkjøp og tomtefeste.',
    icon: 'Map',
    status: 'active',
    route: '/eiendomsrett',
    badge: 'Ny'
  },
  {
    id: 'arverett',
    title: 'Arverett',
    description: 'Sikring av fremtiden',
    shortSell: 'Testament, fremtidsfullmakt og arveoppgjør.',
    icon: 'ScrollText',
    status: 'active',
    route: '/arverett',
    badge: 'Ny'
  },
  {
    id: 'trygderett',
    title: 'Trygderett',
    description: 'NAV og sosiale rettigheter',
    shortSell: 'Klage på NAV, sykepenger og stønad.',
    icon: 'Heart',
    status: 'active',
    route: '/trygderett',
    badge: 'Ny'
  },
  {
    id: 'familierett',
    title: 'Familierett',
    description: 'Samvær og barnebidrag',
    shortSell: 'Skilsmisse og samvær.',
    icon: 'Users',
    status: 'coming',
    route: null,
    badge: null
  }
];

// ============================================================
// IKON-MAPPING
// ============================================================
export const ICON_MAP = {
  FileText,
  Users,
  Clock,
  Sparkles,
  Shield,
  Building2,
  Briefcase,
  Home,
  Map,
  ScrollText,
  Heart,
  Scale,
  Gavel,
  Info,
  CheckCircle
};

// ============================================================
// "KOMMER SNART" MODAL TEMPLATE
// ============================================================
const ComingSoonModal = ({ title, description }) => (
  <div className="space-y-4">
    <p className="text-[var(--text-muted)] leading-relaxed">{description}</p>
    <div className="bg-[var(--copper)]/10 border border-[var(--copper)]/20 rounded-lg p-4">
      <p className="text-sm text-[var(--text-main)]">
        <strong className="text-[var(--copper)]">Kommer snart:</strong> Denne modulen er under utvikling og vil bli tilgjengelig i en fremtidig oppdatering.
      </p>
    </div>
  </div>
);

// ============================================================
// MODAL INNHOLD
// ============================================================
export const MODAL_CONTENT = {
  // AKTIVE MODULER - bruker refaktorerte komponenter
  barnevern: {
    title: 'Barnevern',
    content: <BarnevernDescription />
  },
  forbrukerrett: {
    title: 'Forbrukerrett',
    content: <ForbrukerrettDescription />
  },
  arbeidsrett: {
    title: 'Arbeidsrett',
    content: <ArbeidsrettDescription />
  },
  husleierett: {
    title: 'Husleierett',
    content: <HusleierettDescription />
  },
  forvaltningsrett: {
    title: 'Forvaltningsrett',
    content: <ForvaltningsrettDescription />
  },
  eiendomsrett: {
    title: 'Eiendomsrett',
    content: <EiendomsrettDescription />
  },
  arverett: {
    title: 'Arverett',
    content: <ArverettDescription />
  },
  trygderett: {
    title: 'Trygderett',
    content: <TrygderettDescription />
  },
  familierett: {
    title: 'Familierett – Kommer snart',
    content: <ComingSoonModal 
      title="Familierett"
      description="Familierett-modulen gir verktøy for saker om skilsmisse, samvær og foreldreansvar."
    />
  },
  
  // INFO-MODALER
  hva: {
    title: 'Hva er RettVerk?',
    content: (
      <div className="space-y-5">
        <p className="text-[var(--text-muted)] leading-relaxed">
          RettVerk er en norsk juridisk assistent som gir deg <strong className="text-[var(--text-main)]">klarhet, trygghet og kontroll</strong> i møte med juridiske dokumenter.
        </p>
        <p className="text-[var(--text-muted)] leading-relaxed">
          Plattformen analyserer innholdet du mottar, identifiserer rettigheter, beregner frister og foreslår profesjonelle svar — <strong className="text-[var(--text-main)]">alt på sekunder</strong>.
        </p>
        <div className="bg-[var(--copper)]/10 border border-[var(--copper)]/20 rounded-lg p-4">
          <p className="text-[var(--text-main)] leading-relaxed text-sm">
            <strong className="text-[var(--copper)]">All behandling skjer lokalt på enheten din.</strong><br />
            Dokumentene dine lagres privat, krypteres og forlater aldri din kontroll. Ingen serverlagring. Ingen deling. Ingen risiko.
          </p>
        </div>
        <p className="text-[var(--text-muted)] leading-relaxed">
          RettVerk utjevner maktbalansen mellom deg og systemet — og gir deg juridisk oversikt <strong className="text-[var(--text-main)]">uten at du trenger juridisk utdanning</strong>.
        </p>
      </div>
    )
  },
  
  tilbyr: {
    title: 'Hva tilbyr RettVerk?',
    content: (
      <div className="space-y-5">
        <FeatureItem icon="🧩" title="Spesialiserte moduler" text="RettVerk dekker flere rettsområder — fra barnevern og forbrukerrett til arbeidsrett og husleierett. Hver modul er bygget med skreddersydde analyser, verktøy og prosessforståelse som gir deg reell juridisk kontroll." />
        <FeatureItem icon="📄" title="Dokumentgenerering" text="Plattformen utformer profesjonelle brev, klager og svar — med korrekte lovhenvisninger og struktur som følger norsk forvaltningspraksis. Du får ferdige dokumenter som kan brukes umiddelbart." />
        <FeatureItem icon="🧠" title="AI-drevet analyse" text="RettVerk trekker ut hovedpoenger, identifiserer rettigheter, vurderer vedtak og foreslår anbefalte steg videre. Alt presenteres i et språk som er lett å forstå, uten at presisjonen går tapt." />
        <FeatureItem icon="⏱️" title="Automatisk fristberegning" text="Klagefrister, reklamasjonsfrister, angrefrist og andre juridiske tidsfrister beregnes automatisk — basert på dokumentet du har lastet opp. Du slipper å tolke lovtekst og datoer selv." />
      </div>
    )
  },
  
  hvorfor: {
    title: 'Hvorfor velge RettVerk?',
    content: (
      <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
        <p className="text-sm text-[var(--text-muted)] mb-2 leading-relaxed">
          Vi har gått fra enkel chatbot til komplett saksbehandling. Her er hva som gjør RettVerk unikt:
        </p>

        <ReasonItem icon="📂" title="Komplett saksbehandling — ikke bare dokumenter" text="Last opp hele saksmapper med e-poster, kontrakter og vedtak. RettVerk ser sammenhengene, identifiserer motstridende informasjon og gir deg en helhetlig vurdering av din juridiske posisjon." />
        <ReasonItem icon="📅" title="Automatisk tidslinje og hendelsesforløp" text="Systemet trekker automatisk ut datoer fra alle opplastede dokumenter og genererer en kronologisk tidslinje. Få full oversikt over hendelsesforløpet i saken din på sekunder, ferdig strukturert for eksport." />
        <ReasonItem icon="🧠" title="AI-drevet analyse som faktisk forstår innholdet" text="RettVerk trekker ut hovedpoenger, identifiserer rettigheter og vurderer vedtak. Dette er dyp juridisk innsikt som identifiserer risiko og muligheter i dine dokumenter — ikke bare tekstoppsummering." />
        <ReasonItem icon="⏱️" title="Automatisk fristberegning" text="Klagefrister, reklamasjonsfrister og angrerett beregnes automatisk basert på dokumentene du laster opp. Du slipper å tolke kompliserte lovtekster og datoer selv — systemet varsler deg når det haster." />
        <ReasonItem icon="⚡" title="Profesjonelle dokumenter på minutter" text="Klagebrev, svarbrev og formelle henvendelser genereres med korrekt juridisk struktur og presise lovhenvisninger. Klar til bruk, profesjonelt formulert og ferdig utfylt av din AI-assistent." />
        <ReasonItem icon="🔒" title="Full personvernkontroll" text="Dine dokumenter er dine. Alt lagres kryptert i ditt private bibliotek. Du har full kontroll over dine data, og de brukes aldri til opplæring av eksterne modeller." />

        <div className="border-t border-[var(--border-subtle)] pt-4 mt-2">
          <p className="text-[10px] text-[var(--text-muted)]/60 uppercase tracking-wider mb-3 font-medium">I tillegg</p>
        </div>
        
        <ReasonItem icon="🇳🇴" title="Norskutviklet og tilpasset norsk lov" text="RettVerk er bygget fra bunnen av for norske dokumenter, norske prosesser og norsk forvaltningspraksis. Ingen generiske AI-modeller. Ingen utenlandske antakelser." />
        <ReasonItem icon="⚖️" title="Juridisk presisjon uten juridisk utdanning" text="RettVerk oversetter komplekse vurderinger til klare, forståelige forklaringer — uten at presisjonen går tapt. Du får innsikt som tidligere var forbeholdt jurister." />
        <ReasonItem icon="📚" title="Spesialiserte moduler for flere rettsområder" text="Barnevern, forbrukerrett, arbeidsrett, husleierett, eiendomsrett, familierett og flere. Hver modul har egne verktøy, analyser og prosessforståelse." />
        <ReasonItem icon="🧩" title="Master-grade verktøy for krevende saker" text="Mangelsveiviser, erstatningskalkulatorer, kontraktanalyse, fremleie-generator, vedlikeholdsmatrise, utkastelsesguide — alt samlet i én plattform." />
        <ReasonItem icon="🎯" title="Utjevner maktbalansen" text="RettVerk gir deg innsikt, struktur og trygghet i møte med offentlige etater, utleiere, arbeidsgivere og andre aktører som vanligvis sitter med mer kunnskap." />
        <ReasonItem icon="🧭" title="Tydelige anbefalinger og neste steg" text="Du får ikke bare informasjon — du får retning. Hva du bør gjøre, når du bør gjøre det, og hvorfor." />
      </div>
    )
  },
  
  hvordan: {
    title: 'Hvordan fungerer det?',
    content: (
      <div className="space-y-4">
        <StepItem num={1} title="Velg rettsområde" text="Du starter med å velge hvilket rettsområde saken gjelder, slik at verktøyet bruker riktig faglig struktur." />
        <StepItem num={2} title="Last opp dokument" text="Du legger inn dokumenter som skal analyseres. All behandling skjer lokalt på enheten din." />
        <StepItem num={3} title="Systemet analyserer innholdet" text="RettVerk identifiserer relevante opplysninger, temaer, rettigheter og potensielle mangler basert på mønstre i teksten. Det foretas ikke juridiske vurderinger i advokatmessig forstand." />
        <StepItem num={4} title="Se rettigheter og frister" text="Du får en oversikt over hvilke rettigheter som kan være aktuelle, samt eventuelle frister som følger av dokumentet eller regelverket." />
        <StepItem num={5} title="Generer utkast til svar eller klage" text="Basert på analysen kan du generere et strukturert utkast som du selv kvalitetssikrer og tilpasser før bruk." />
      </div>
    )
  },
  
  personvern: {
    title: 'Personvern og sikkerhet',
    content: (
      <div className="space-y-3">
        <PrivacyItem icon="🛡️" title="Lagres kun lokalt på din enhet" text="Alle dokumenter, analyser og filer behandles og lagres utelukkende på din egen enhet. Ingen data sendes til eller lagres på eksterne servere." />
        <PrivacyItem icon="🔐" title="AES-256-GCM kryptering" text="Dokumenter og metadata krypteres lokalt med AES-256-GCM, en moderne og anerkjent krypteringsstandard brukt i profesjonelle sikkerhetsmiljøer." />
        <PrivacyItem icon="🚫" title="Ingen serverlagring av dokumenter" text="RettVerk har ingen tilgang til innholdet i dokumentene dine. Ingenting lastes opp, prosesseres eller lagres hos oss." />
        <PrivacyItem icon="🔍" title="Ingen sporing eller analyse" text="RettVerk samler ikke inn bruksdata, logger, dokumentinnhold eller annen informasjon om hvordan du bruker tjenesten. Det foregår ingen sporing, profilering eller ekstern analyse." />
      </div>
    )
  },
  
  terms: {
    title: 'Vilkår og ansvar',
    content: (
      <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-2">
        <div className="bg-[var(--copper)]/10 border border-[var(--copper)]/20 rounded-lg p-4">
          <p className="text-sm text-[var(--text-main)] leading-relaxed">
            <strong className="text-[var(--copper)]">RettVerk er et juridisk støtteverktøy.</strong> Tjenesten gir strukturerte analyser, forslag til formuleringer og dokumentutkast basert på mønstre i teksten du legger inn. RettVerk foretar ikke juridiske vurderinger i advokatmessig forstand og erstatter ikke profesjonell juridisk rådgivning.
          </p>
        </div>

        <TermsSection icon="👤" title="Brukerens ansvar">
          <TermsItem title="Riktige opplysninger" text="Du er selv ansvarlig for riktigheten og fullstendigheten av alle opplysninger du legger inn i systemet og i dokumentene som genereres." />
          <TermsItem title="Kvalitetssikring" text="Du er ansvarlig for å gjennomgå, kontrollere og kvalitetssikre alle analyser og dokumenter før de tas i bruk eller sendes til en tredjepart." />
          <TermsItem title="Juridisk rådgivning ved tvil" text="Ved komplekse, tvetydige eller prinsipielle spørsmål anbefales det at du innhenter juridisk bistand fra advokat." />
        </TermsSection>

        <TermsSection icon="⚖️" title="Avgrensning av tjenesten">
          <div className="p-3 rounded-lg bg-[var(--bg-highlight)]/30 border border-[var(--border-subtle)]">
            <p className="text-xs text-[var(--text-muted)] mb-2">RettVerk:</p>
            <ul className="text-xs text-[var(--text-muted)] space-y-1 ml-4">
              <li>• gir ingen garanti for fullstendighet, riktighet eller juridisk gyldighet i analyser eller dokumenter</li>
              <li>• tar ikke stilling til bevisvurdering, rettsanvendelse eller strategiske juridiske valg</li>
              <li>• gir ikke individuelle råd tilpasset din konkrete sak</li>
              <li>• kan ikke holdes ansvarlig for hvordan analyser eller dokumenter brukes i praksis</li>
            </ul>
          </div>
        </TermsSection>

        <TermsSection icon="🚫" title="Ansvarsbegrensning">
          <div className="p-3 rounded-lg bg-[var(--bg-highlight)]/30 border border-[var(--border-subtle)]">
            <p className="text-xs text-[var(--text-muted)] mb-2">RettVerk fraskriver seg ansvar for:</p>
            <ul className="text-xs text-[var(--text-muted)] space-y-1 ml-4">
              <li>• direkte eller indirekte tap</li>
              <li>• økonomiske konsekvenser</li>
              <li>• prosessuelle feil</li>
              <li>• manglende frister</li>
              <li>• tap av rettigheter</li>
              <li>• eller andre følger som oppstår ved bruk av tjenesten</li>
            </ul>
            <p className="text-xs text-[var(--text-main)] mt-3 font-medium">Brukeren bærer selv risikoen ved å benytte analyser og dokumenter generert av systemet.</p>
          </div>
        </TermsSection>

        <TermsSection icon="🔒" title="Personvern og databehandling">
          <div className="p-3 rounded-lg bg-[var(--bg-highlight)]/30 border border-[var(--border-subtle)]">
            <p className="text-xs text-[var(--text-muted)] leading-relaxed">
              RettVerk er bygget etter <strong className="text-[var(--text-main)]">Privacy first</strong>-prinsipper. All behandling skjer lokalt på din enhet med mindre du aktivt velger å dele data. RettVerk har ikke tilgang til innholdet i dokumentene dine uten ditt uttrykkelige samtykke. Du anses som behandlingsansvarlig for dokumentene du oppretter og lagrer.
            </p>
          </div>
        </TermsSection>

        <div className="pt-3 border-t border-[var(--border-subtle)]">
          <p className="text-xs text-[var(--text-main)] text-center font-medium">
            Ved å bruke RettVerk bekrefter du at du har lest, forstått og akseptert disse vilkårene og ansvarsbegrensningene.
          </p>
        </div>
      </div>
    )
  }
};

// ============================================================
// HJELPEFUNKSJONER FOR MODAL-INNHOLD
// ============================================================
function FeatureItem({ icon, title, text }) {
  return (
    <div className="p-4 rounded-lg bg-[var(--bg-highlight)]/30 border border-[var(--border-subtle)]">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        <h3 className="font-medium text-[var(--text-main)]">{title}</h3>
      </div>
      <p className="text-sm text-[var(--text-muted)] leading-relaxed">{text}</p>
    </div>
  );
}

function ReasonItem({ icon, title, text }) {
  return (
    <div className="p-3 rounded-lg bg-[var(--bg-highlight)]/30 border border-[var(--border-subtle)]">
      <h3 className="font-medium text-[var(--text-main)] mb-1 flex items-center gap-2">
        <span>{icon}</span> {title}
      </h3>
      <p className="text-sm text-[var(--text-muted)]">{text}</p>
    </div>
  );
}

function StepItem({ num, title, text }) {
  return (
    <div className="flex gap-4">
      <span className="w-8 h-8 rounded-full bg-[var(--copper)]/20 text-[var(--copper)] flex items-center justify-center text-sm font-semibold flex-shrink-0">{num}</span>
      <div>
        <h4 className="font-medium text-[var(--text-main)] mb-1">{title}</h4>
        <p className="text-sm text-[var(--text-muted)]">{text}</p>
      </div>
    </div>
  );
}

function PrivacyItem({ icon, title, text }) {
  return (
    <div className="p-4 rounded-lg bg-[var(--bg-highlight)]/30 border border-[var(--border-subtle)]">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-[var(--success)]" />
        <h4 className="font-medium text-[var(--text-main)]">{title}</h4>
      </div>
      <p className="text-sm text-[var(--text-muted)]">{text}</p>
    </div>
  );
}

function TermsSection({ icon, title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="font-medium text-[var(--text-main)] flex items-center gap-2">
        <span>{icon}</span> {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TermsItem({ title, text }) {
  return (
    <div className="p-3 rounded-lg bg-[var(--bg-highlight)]/30 border border-[var(--border-subtle)]">
      <h4 className="text-sm font-medium text-[var(--text-main)] mb-1">{title}</h4>
      <p className="text-xs text-[var(--text-muted)]">{text}</p>
    </div>
  );
}

// ============================================================
// MENY-ELEMENTER
// ============================================================
export const MENU_ITEMS = [
  { id: 'hva', label: 'Hva er RettVerk?' },
  { id: 'tilbyr', label: 'Hva tilbyr RettVerk?' },
  { id: 'hvorfor', label: 'Hvorfor velge RettVerk?' },
  { id: 'hvordan', label: 'Hvordan fungerer det?' },
  { id: 'personvern', label: 'Personvern og sikkerhet' },
  { id: 'terms', label: 'Vilkår og ansvar' }
];

// ============================================================
// HJELPEFUNKSJONER
// ============================================================
export function getModuleById(id) {
  return LAW_MODULES.find(m => m.id === id);
}

export function getModalContent(id) {
  return MODAL_CONTENT[id];
}
